<script setup>
import BaseButton from "@/app/components/ui/BaseButton.vue"
import BaseSelectField from "@/app/components/ui/BaseSelectField.vue"
import BaseTextField from "@/app/components/ui/BaseTextField.vue"
import { useI18n } from "@/app/composables/useI18n"
import { toast } from "@/app/utils/notification"
import { computed, ref, watch } from "vue"

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  editMode: {
    type: Boolean,
    default: false,
  },
  limitData: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(["close", "save"])

const { t } = useI18n()

// Form data
const formData = ref({
  type: "session", // Default to session type
  period: "",
  value: "",
})

// Make dropdown options reactive to language changes
const limitTypes = computed(() => [
  { label: t("responsible_gambling.session") || "Session", value: "session" },
  { label: t("responsible_gambling.bet") || "Bet", value: "bet" },
  { label: t("responsible_gambling.deposit") || "Deposit", value: "deposit" },
])

const periods = computed(() => [
  { label: t("account.day") || "Day", value: "day" },
  { label: t("account.week") || "Week", value: "week" },
  { label: t("account.month") || "Month", value: "month" },
])

// Computed properties for dynamic UI
const isSessionType = computed(() => formData.value.type === "session")
const isBetType = computed(() => formData.value.type === "bet")
const isDepositType = computed(() => formData.value.type === "deposit")

const valueLabel = computed(() => {
  return isSessionType.value
    ? (t("account.limit_value_minutes") || "Limit Value (minutes)")
    : (t("account.limit_value_usd") || "Limit Value (USD)")
})

const valuePlaceholder = computed(() => {
  return isSessionType.value
    ? (t("account.enter_minutes") || "Enter minutes")
    : (t("account.enter_amount") || "Enter amount")
})

const valueUnit = computed(() => {
  return isSessionType.value ? "min" : "USD"
})

const modalTitle = computed(() => {
  const baseTitle = props.editMode ? (t("common.edit") || "Edit") : (t("common.create") || "Create")
  const limitTypeText = isSessionType.value
    ? (t("responsible_gambling.session") || "Session")
    : (isBetType.value ? (t("responsible_gambling.bet") || "Bet") : (t("responsible_gambling.deposit") || "Deposit"))
  const limitText = t("account.limit") || "Limit"
  return `${baseTitle} ${limitTypeText} ${limitText}`
})

// Watch for type changes to reset value
watch(() => formData.value.type, (newType, oldType) => {
  if (newType !== oldType) {
    formData.value.value = ""
  }
})

watch(
  () => props.limitData,
  (newData) => {
    if (newData && Object.keys(newData).length > 0) {
      formData.value = {
        type: newData.type || "session",
        period: newData.period || "",
        value: newData.value || "",
      }
    }
  },
  {
    immediate: true,
    deep: true,
  },
)

function resetForm() {
  formData.value = {
    type: "session", // Default session type
    period: "",
    value: "",
  }
}

function closeModal() {
  emit("close")
  resetForm()
}

function validateForm() {
  if (!formData.value.type) {
    toast.error(t("responsible_gambling.select_limit_type") || "Please select a limit type")
    return false
  }
  if (!formData.value.period) {
    toast.error(t("responsible_gambling.select_period") || "Please select a time period")
    return false
  }
  if (!formData.value.value || formData.value.value <= 0) {
    toast.error(t("responsible_gambling.enter_valid_value") || "Please enter a valid value")
    return false
  }

  // Additional validation for session type (minutes should be reasonable)
  if (isSessionType.value) {
    const minutes = Number.parseInt(formData.value.value)
    if (minutes < 1 || minutes > 1440) { // 1 minute to 24 hours
      toast.error(t("responsible_gambling.session_limit_range") || "Session limit must be between 1 and 1440 minutes")
      return false
    }
  }

  // Additional validation for bet type (amount should be reasonable)
  if (isBetType.value) {
    const amount = Number.parseFloat(formData.value.value)
    if (amount < 0.01 || amount > 1000000) { // $0.01 to $1M
      toast.error(t("responsible_gambling.bet_limit_range") || "Bet limit must be between $0.01 and $1,000,000")
      return false
    }
  }

  // Additional validation for deposit type (amount should be reasonable)
  if (isDepositType.value) {
    const amount = Number.parseFloat(formData.value.value)
    if (amount < 0.01 || amount > 1000000) { // $0.01 to $1M
      toast.error(t("responsible_gambling.deposit_limit_range") || "Deposit limit must be between $0.01 and $1,000,000")
      return false
    }
  }

  return true
}

function handleSave() {
  if (validateForm()) {
    // Send only the required fields for the REST API
    emit("save", {
      type: formData.value.type,
      period: formData.value.period,
      value: formData.value.value,
    })

    closeModal()
  }
}
</script>

<template>
  <Transition name="modal">
    <div v-if="show" class="modal-mask" data-testid="sessionLimitModal">
      <div class="modal-container">
        <div class="modal-detail">
          <div class="close-btn" @click="closeModal">
            <div>{{ t("general.close") || "Close" }}</div>
            <img src="/assets/images/close-modal-icon.svg" alt="">
          </div>
          <div class="modal-body">
            <div class="modal-header">
              <h3>{{ modalTitle }}</h3>
            </div>

            <div class="form-section">
              <BaseSelectField
                v-model="formData.type"
                :label="t('finance.type') || 'Type'"
                :options="limitTypes"
                :placeholder="t('account.select_limit_type') || 'Select limit type'"
              />
            </div>

            <div class="form-section">
              <BaseSelectField
                v-model="formData.period"
                :label="t('account.time_period') || 'Period'"
                :options="periods"
                :placeholder="t('account.select_period') || 'Select period'"
              />
            </div>

            <div class="form-section">
              <BaseTextField
                v-model="formData.value"
                :label="valueLabel"
                type="number"
                :placeholder="valuePlaceholder"
                :step="isSessionType ? '1' : '0.01'"
                :min="isSessionType ? '1' : '0.01'"
              />
              <div class="value-unit">
                {{ valueUnit }}
              </div>
            </div>

            <div class="modal-actions">
              <BaseButton class="save-btn alight wide" @click="handleSave">
                {{ editMode ? (t("common.update") || "Update") : (t("common.create") || "Create") }}
              </BaseButton>
              <BaseButton class="cancel-btn" @click="closeModal">
                {{ t("common.cancel") || "Cancel" }}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(5px);
  display: flex;
  transition: opacity 0.3s ease;
}

.close-btn {
  color: white;
  position: absolute;
  top: -32px;
  right: 3px;
  display: flex;
  gap: 0 5px;
  cursor: pointer;
}

.modal-container {
  width: 491px;
  margin: auto;
  padding: 39px 20px 20px 20px;
  height: 100%;
  overflow: auto;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
}

.modal-detail {
  background: var(--be-bg);
  padding: 20px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  margin-top: 13px;
  border-radius: 20px;
  margin: auto;
  position: relative;
  width: 100%;
}

.modal-body {
  border-radius: 8px;
  background: var(--be-big-card-bg);
  padding: 24px;
}

.modal-header {
  margin-bottom: 24px;

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: var(--be-primary-text-color);
    text-align: center;
  }
}

.form-section {
  margin-bottom: 20px;
  position: relative;

  .value-unit {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 14px;
    color: var(--be-text-secondary);
    pointer-events: none;
    margin-top: 15px; // Adjust for label spacing
  }

      // Override vue-select dropdown styling to match modal theme
  :deep(.base-select-field) {
    outline: none !important;
    border: none !important;

    .vs__dropdown-toggle {
      background-color: var(--be-small-card-bg) !important;
      border: 1px solid transparent !important;
      border-radius: 4px !important;
      outline: none !important;
      box-shadow: none !important;

      &:focus-within {
        border: 1px solid var(--be-primary-btn, #3886ec) !important;
        outline: none !important;
        box-shadow: none !important;
      }

      .vs__selected-options {
        color: var(--be-primary-text-color) !important;
      }

      .vs__search {
        color: var(--be-primary-text-color) !important;
        outline: none !important;
        border: none !important;
        box-shadow: none !important;

        &::placeholder {
          color: var(--be-text-secondary) !important;
        }

        &:focus {
          outline: none !important;
          border: none !important;
          box-shadow: none !important;
        }
      }

      .vs__actions {
        .vs__open-indicator {
          color: var(--be-text-secondary) !important;
        }
      }
    }

        .vs__dropdown-menu {
      background-color: var(--be-small-card-bg) !important;
      border: 1px solid transparent !important;
      border-radius: 4px !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
      outline: none !important;

      .vs__dropdown-option {
        color: var(--be-primary-text-color) !important;
        background-color: transparent !important;
        border: none !important;
        outline: none !important;

        &:hover {
          background-color: var(--be-bg) !important;
          color: var(--be-primary-text-color) !important;
          outline: none !important;
        }

        &--highlight {
          background-color: var(--be-bg) !important;
          color: var(--be-primary-text-color) !important;
          outline: none !important;
        }

        &:focus {
          outline: none !important;
          box-shadow: none !important;
        }
      }
    }
  }

    // Also style text inputs to match
  :deep(.input-element) {
    .label {
      color: var(--be-primary-text-color) !important;
      font-weight: 500 !important;
    }

    input {
      background-color: var(--be-small-card-bg) !important;
      border: 1px solid transparent !important;
      color: var(--be-primary-text-color) !important;
      border-radius: 4px !important;
      outline: none !important;
      box-shadow: none !important;
      opacity: 1 !important;
      font-size: 15px !important;

      &:focus {
        border: 1px solid var(--be-primary-btn, #3886ec) !important;
        outline: none !important;
        box-shadow: none !important;
      }

      &::placeholder {
        color: var(--be-text-secondary) !important;
      }
    }
  }
}

.modal-actions {
  display: flex;
  gap: 12px;
  margin-top: 32px;

  .cancel-btn {
    flex: 1;
    background: var(--be-secondary-btn);
    color: var(--be-secondary-btn-label);

    &:hover {
      background: var(--be-secondary-btn-hover);
    }
  }

  .save-btn {
    flex: 2;

    &:hover {
      opacity: 0.9;
    }
  }
}

// Modal transition styles
.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

@media screen and (max-width: 768px) {
  .modal-container {
    width: 95%;
    padding: 20px 10px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-actions {
    flex-direction: column;

    .cancel-btn,
    .save-btn {
      flex: 1;
    }
  }
}
</style>
